import React, { useState } from 'react';
import './styles/App.css';
import bgImage from './images/bg_s_3.jpeg';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { supabase } from './lib/supabase';

function App() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isJoined, setIsJoined] = useState(false);

  // Get user's IP address
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error getting IP:', error);
      return '0.0.0.0'; // Fallback IP
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Get user's IP address
      const userIP = await getUserIP();

      // Check if IP is blocked
      const { data: ipCheck, error: ipError } = await supabase
        .from('email_waitlist')
        .select('is_blocked, submission_count')
        .eq('ip_address', userIP)
        .single();

      if (ipError && ipError.code !== 'PGRST116') { // PGRST116 = no rows found
        throw ipError;
      }

      // Block if IP has been flagged
      if (ipCheck && ipCheck.is_blocked) {
        setMessage('Too many attempts. Please try again later.');
        return;
      }

      // Block if too many submissions from same IP (5+ attempts)
      if (ipCheck && ipCheck.submission_count >= 5) {
        // Mark IP as blocked
        await supabase
          .from('email_waitlist')
          .update({ is_blocked: true })
          .eq('ip_address', userIP);
        
        setMessage('Too many attempts. Please try again later.');
        return;
      }

      // Try to insert new email
      const { data, error } = await supabase
        .from('email_waitlist')
        .insert([
          {
            email: email.toLowerCase().trim(),
            ip_address: userIP,
            submission_count: 1
          }
        ])
        .select();

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === '23505') { // Unique violation
          setMessage('This email is already on our waitlist!');
        } else {
          throw error;
        }
      } else {
        setMessage('Thank you! You have been added to the waitlist.');
        setEmail('');
        setIsJoined(true);
      }
      
    } catch (error) {
      console.error('Error subscribing:', error);
      setMessage('Could not submit email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 10%',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        width: '100%',
        position: 'relative'
      }}
    >
      {/* Background overlay for better text readability - TEMPORARILY REMOVED TO TEST */}
      {/* <div className="absolute inset-0 bg-black bg-opacity-50"></div> */}

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 p-6 md:p-8 z-10">
        <nav className="flex justify-between items-center">
          {/* Brand name with Montserrat SemiBold 600 Italic */}
          <h1 className="text-2xl md:text-3xl montserrat-brand tracking-wider text-white">BOR-DES</h1>
          {/* Commented out until functionality is implemented */}
          {/* 
          <div className="flex items-center space-x-6">
            <a href="#" className="text-sm font-medium text-white hover:text-gray-300 transition-colors">Login</a>
            <div className="flex items-center space-x-4">
              <a href="#" className="text-white hover:text-gray-300 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-gray-300 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.71v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-gray-300 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.048-1.067-.06-1.407-.06-4.123v-.08c0-2.643.012-2.987.06-4.043.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 016.345 2.525c.636-.247 1.363-.416 2.427-.465C9.793 2.013 10.133 2 12.775 2h.08zM12 6.845a5.155 5.155 0 100 10.31 5.155 5.155 0 000-10.31zm0 8.315a3.16 3.16 0 110-6.32 3.16 3.16 0 010 6.32zm6.306-6.701a1.25 1.25 0 10-2.5 0 1.25 1.25 0 002.5 0z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
          */}
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center text-center max-w-4xl w-full px-6 md:px-8 h-screen justify-center">
        <div className="flex flex-col items-center">
          <h2 className="fixed-coming-soon-size font-light tracking-widest text-white mb-6">COMING SOON</h2>
          <div className="max-w-sm sm:max-w-md w-full h-[140px] sm:h-[120px] flex flex-col justify-center px-4 sm:px-0">
            {!isJoined ? (
              <>
                <p className="fixed-text-size text-white mb-4 font-medium">
                  Join the community
                </p>
                <form onSubmit={handleSubmit} className="enhanced-form flex flex-col sm:flex-row w-full gap-3 sm:gap-2 max-w-md">
                  <Input 
                    type="email" 
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                    className="flex-1 rounded-md glow-input w-full"
                    disabled={isLoading}
                  />
                  <button 
                    type="submit" 
                    className="liquid-glass-button w-full sm:w-auto sm:px-6"
                    disabled={isLoading}
                  >
                    Join
                  </button>
                </form>
              </>
            ) : (
              <div className="welcome-text blur-to-glow">
                WELCOME
              </div>
            )}
          </div>
          <div className="mt-4 text-base h-6">
            {message && (
              <p className={`font-medium ${
                message.includes('Thank you') 
                  ? 'text-green-300 blur-to-glow-message' 
                  : message.includes('Please enter') || message.includes('Could not')
                    ? 'text-red-300'
                    : 'text-white opacity-90'
              }`}>
                {message}
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;